import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";

const SUBMIT_URL = "https://readdy.ai/api/form/da0qniu859p3u981e5p0";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();
    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    if (!name || !email || !message) {
      setFormError("请填写姓名、邮箱和留言内容后再提交。");
      setStatus("error");
      return;
    }
    if (message.length > 500) {
      setFormError("留言内容请控制在 500 字以内。");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setFormError("");
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "website_alt" && value) body.append(key, String(value));
      });
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const code = parsed?.code as string | undefined;
      const serverMsg = String(meta?.message ?? meta?.detail ?? parsed?.message ?? responseText ?? "").toLowerCase();
      if (res.ok && code === "OK" && !serverMsg.includes("spam")) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(String(meta?.message ?? meta?.detail ?? parsed?.message ?? "提交失败，请稍后再试。"));
        setStatus("error");
      }
    } catch {
      setFormError("网络似乎开小差了，请稍后再试。");
      setStatus("error");
    }
  };

  return (
    <Layout>
      <title>联系我们 - 分享你的快乐水追更故事 | 可乐漫画</title>
      <meta name="description" content="联系我们：可乐漫画欢迎你分享追更心得、快乐水组合创意与肥宅快乐套餐灵感。填写表单或通过邮箱、社交平台与我们联系。" />
      <meta name="keywords" content="可乐漫画,联系我们,快乐水组合,肥宅快乐套餐,追更配零食" />
      <link rel="canonical" href="https://lazymix.github.io/contact" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "联系可乐漫画",
          url: "https://lazymix.github.io/contact",
        })}
      </script>

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <span className="text-foreground-800">联系我们</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-3 max-w-2xl text-sm text-foreground-600 md:text-base">
            想分享你的追更心得、快乐水组合创意，或是对可乐漫画有任何建议？欢迎给我们留言。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-5">
          {/* 表单 */}
          <div className="lg:col-span-3">
            <form
              data-readdy-form="可乐漫画联系表单"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
            >
              <div className="website-field-trap" aria-hidden="true">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-950">你的称呼</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    placeholder="怎么称呼你"
                    className="mt-2 w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-950">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="mt-2 w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-950">主题</label>
                <select
                  id="contact-subject"
                  name="subject"
                  className="mt-2 w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="追更心得">追更心得</option>
                  <option value="快乐水搭配灵感">快乐水搭配灵感</option>
                  <option value="合作与建议">合作与建议</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-950">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="写下你想说的话（500 字以内）"
                  className="mt-2 w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
                <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
              </div>

              {status === "error" && (
                <p className="mt-4 rounded-md border border-primary-300 bg-primary-100/60 px-4 py-3 text-sm text-primary-800">
                  {formError}
                </p>
              )}
              {status === "success" && (
                <p className="mt-4 rounded-md border border-secondary-200 bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  感谢你的留言！我们会尽快回复，愿你的快乐水追更时光越来越快乐。
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {status === "loading" ? "发送中..." : "提交留言"}
                <i className="ri-send-plane-fill" style={{ width: 16, height: 16 }} />
              </button>
            </form>
          </div>

          {/* 联系方式 */}
          <div className="space-y-5 lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 text-base font-medium text-foreground-950">
                <i className="ri-cup-fill text-primary-500" style={{ width: 18, height: 18 }} />
                直接联系
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-primary-600" style={{ width: 16, height: 16 }} />
                  hello@cola-comics.com
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line text-primary-600" style={{ width: 16, height: 16 }} />
                  工作日 9:00 - 18:00
                </li>
              </ul>
              <div className="mt-5 flex gap-3">
                {["ri-wechat-fill", "ri-weibo-fill", "ri-bilibili-fill", "ri-tiktok-fill"].map((icon) => (
                  <a
                    key={icon}
                    href="#contact"
                    aria-label="社交平台"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-background-200 text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50"
                  >
                    <i className={icon} style={{ width: 16, height: 16 }} />
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 text-base font-medium text-foreground-950">
                <i className="ri-question-line text-primary-500" style={{ width: 18, height: 18 }} />
                常见问题
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                想了解免费阅读、追更榜单更新频率等问题？也许能在常见问题页面找到答案。
              </p>
              <Link
                to="/faq"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-600"
              >
                去查看常见问题
                <i className="ri-arrow-right-line" style={{ width: 16, height: 16 }} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}